import { useQuery } from '@tanstack/react-query';
import { apis } from 'apis/apis';
import { TCreatorReportProcess } from 'type/db';

export const useGetCreatorReportList = (
	groupId: string,
	progress: TCreatorReportProcess,
) => {
	return useQuery({
		queryKey: ['creator-report', groupId, progress],
		queryFn: () => apis.getCreatorReportsList(groupId, progress),
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: groupId !== null,
	});
};

export const useGetCreatorProfile = (creatorPk: string | undefined) => {
	return useQuery({
		queryKey: ['creator-profile', creatorPk],
		queryFn: () => apis.getCreatorProfile(creatorPk ?? ''),
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: creatorPk !== undefined,
	});
};

export const useGetCreatorGroupSummary = (creatorPk?: string, groupId?: string) => {
	return useQuery({
		queryKey: ['creator-group-summary', creatorPk, groupId],
		queryFn: () => {
			if (creatorPk === undefined || groupId === undefined) {
				return undefined;
			}
			return apis.getCreatorGroupSummary(creatorPk, groupId);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!creatorPk && !!groupId,
	});
};

export const useGetCreatorMissionPerformanceDaily = (
	creatorPk?: string,
	groupId?: string,
	missionNumber?: string,
) => {
	return useQuery({
		queryKey: ['creator-mission-performance-daily', creatorPk, groupId, missionNumber],
		queryFn: () => {
			if (
				creatorPk === undefined ||
				groupId === undefined ||
				missionNumber === undefined
			) {
				return undefined;
			}
			return apis.getCreatorMissionPerformanceDaily(creatorPk, groupId, missionNumber);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!creatorPk && !!groupId && !!missionNumber,
	});
};

export const useGetCreatorGroupPerformances = (groupId?: string, creatorPk?: string) => {
	return useQuery({
		queryKey: ['creator-group-performances', creatorPk, groupId],
		queryFn: () => {
			if (creatorPk === undefined || groupId === undefined) {
				return undefined;
			}
			return apis.getCreatorGroupPerformances(creatorPk, groupId);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!creatorPk && !!groupId,
	});
};

export const useGetCreatorContents = (creatorPk?: string) => {
	return useQuery({
		queryKey: ['creator-contents', creatorPk],
		queryFn: () => {
			if (creatorPk === undefined) {
				return Promise.resolve([]);
			}
			return apis.getCreatorContents(creatorPk);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!creatorPk,
	});
};

export const useGetCreatorContentDetails = (creatorPk?: string, contentPk?: number) => {
	return useQuery({
		queryKey: ['creator-content-details', creatorPk, contentPk],
		queryFn: () => {
			if (creatorPk === undefined || contentPk === undefined) {
				return undefined;
			}
			return apis.getCreatorContentDetails(creatorPk, contentPk);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!creatorPk && contentPk !== undefined,
	});
};

export const useGetCreatorGroups = (creatorPk?: string) => {
	return useQuery({
		queryKey: ['creator-groups', creatorPk],
		queryFn: () => {
			if (creatorPk === undefined) {
				return undefined;
			}
			return apis.getCreatorGroups(creatorPk);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!creatorPk,
	});
};
